// src/pages/PortalDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import portalApi from '../utils/portalApi';
import { usePortalAuth } from '../context/PortalAuthContext';

export default function PortalDashboard() {
  const { member } = usePortalAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi.get('/portal/dashboard')
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;
  if (!data) return <div>Failed to load dashboard.</div>;

  const owed = parseFloat(data.invoiceSummary?.total_owed || 0);
  const openInvoices = parseInt(data.invoiceSummary?.open_count || 0);
  const upcomingTickets = parseInt(data.ticketSummary?.upcoming_tickets || 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Welcome back, {member?.firstName}</h1>
          <p className="page-subtitle">{data.business?.business_name}</p>
        </div>
      </div>

      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Membership Status</div>
          <div className="stat-value" style={{ fontSize: '1.5rem' }}>
            <span className={`badge badge-${data.business?.status}`}>{data.business?.status?.replace('_', ' ')}</span>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 6 }}>
            {data.business?.membership_type || 'Member'}
            {data.business?.next_renewal_date && (
              <> · renews {fmtDate(data.business.next_renewal_date)}</>
            )}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open Invoices</div>
          <div className="stat-value" style={{ color: openInvoices > 0 ? 'var(--danger)' : 'var(--gray-700)' }}>
            {openInvoices}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 6 }}>
            ${owed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} due
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Upcoming Tickets</div>
          <div className="stat-value">{upcomingTickets}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 6 }}>
            <Link to="/portal/tickets">View tickets</Link>
          </div>
        </div>
      </div>

      {/* Open invoices card */}
      {openInvoices > 0 && (
        <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--danger)' }}>
          <div className="card-header">
            <span className="card-title">You have outstanding invoices</span>
            <Link to="/portal/invoices" className="btn btn-primary btn-sm">View All</Link>
          </div>
          <div className="card-body">
            <p style={{ margin: 0 }}>
              <strong>${owed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> outstanding across {openInvoices} invoice{openInvoices === 1 ? '' : 's'}.
            </p>
          </div>
        </div>
      )}

      {/* Upcoming events */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Upcoming Events</span>
          <Link to="/portal/events" className="btn btn-secondary btn-sm">See all</Link>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {(!data.upcomingEvents?.length) ? (
            <p style={{ padding: 16, color: 'var(--gray-500)', margin: 0 }}>No upcoming events at this time.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Date</th>
                  <th>Venue</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.upcomingEvents.map(ev => (
                  <tr key={ev.id}>
                    <td className="td-primary">{ev.name}</td>
                    <td>{fmtDate(ev.event_date)}{ev.start_time ? ` · ${fmtTime(ev.start_time)}` : ''}</td>
                    <td style={{ color: 'var(--gray-600)' }}>{ev.venue || '—'}</td>
                    <td>
                      {parseInt(ev.already_registered) > 0 ? (
                        <span className="badge badge-active" style={{ fontSize: '0.7rem' }}>Registered</span>
                      ) : (
                        <Link to="/portal/events" className="btn btn-secondary btn-sm">Register</Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtTime(t) {
  if (!t) return '';
  // t is "HH:MM:SS"
  const [h, m] = t.split(':');
  const hh = parseInt(h, 10);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}
